/**
 * Session replay (AI-3016).
 *
 * Everything `track()` cannot answer: not what an employer clicked, but where
 * they hesitated, misread a control, or backed out. Capture is browser-side —
 * it cannot ride the server relay in `analytics.ts` — so this is the first
 * third-party SDK to run in chat-emp's bundle, and most of this file is about
 * keeping that safe and cheap rather than about recording.
 *
 * FOUR RULES IT EXISTS TO HOLD:
 *
 * 1. No upstream reaches the browser. PostHog is proxied at `/api/ph` (see
 *    `server/src/routes/posthog.ts`), so `script-src 'self'` and
 *    `connect-src 'self'` stand unchanged and `security-headers.ts` is untouched.
 * 2. No credential is ever recorded. Network capture stays off — every fetch in
 *    this app carries the session JWT, and recording headers or bodies would
 *    write a live credential into a replay.
 * 3. Nothing loads for employers who never open chat. The SDK is imported
 *    dynamically, so its chunk is fetched on the first real chat open and never
 *    on the widget launcher, which mounts on every page across the site.
 * 4. Production only. No key, no SDK, no request — which is the whole of "dev
 *    records nothing", matching how stream-chat-backend gates PostHog.
 */

import type { CaptureResult } from 'posthog-js';

const KEY = import.meta.env.VITE_POSTHOG_KEY ?? '';

/** Same-origin. The BFF splits assets from ingestion behind this one path. */
const API_HOST = '/api/ph';

/** Error messages can quote dirty upstream data, so only this much leaves the browser. */
const EXCEPTION_MESSAGE_MAX = 500;

type Posthog = typeof import('posthog-js').posthog;

let started: Promise<Posthog | null> | null = null;
let live: Posthog | null = null;

/**
 * Elements whose text is replaced in the recording. Marked at the component
 * rather than listed here, so a new screen inherits the rule by reusing the
 * component instead of by someone remembering this file.
 *
 * rrweb checks ancestors, so marking a container covers its subtree.
 */
const MASK_TEXT = '[data-ph-mask]';
/** Elements replaced wholesale — avatars, where the pixels ARE the identity. */
const BLOCK = '[data-ph-block]';

/**
 * Begin recording, once per page.
 *
 * `distinctId` must be the id the server already stamps on relayed events (the
 * company id) or replays and events land under two different people in PostHog
 * and cannot be joined — which is the only reason to record at all. It comes
 * from the verified session, never from bridge or query input.
 */
export function startReplay(distinctId: string): Promise<Posthog | null> {
  if (started) return started;
  if (!KEY || !distinctId || typeof window === 'undefined') {
    started = Promise.resolve(null);
    return started;
  }

  started = import('posthog-js')
    .then(({ posthog }) => {
      posthog.init(KEY, {
        api_host: API_HOST,
        // Identity is the server's, decided before a single event is sent, so
        // there is never a window where this records as an anonymous stranger.
        bootstrap: { distinctID: distinctId },

        // COST. Autocapture bills an event for every click, change and submit
        // anywhere in the app — it is on by default and it is the single most
        // expensive switch here. chat-emp names what it tracks, deliberately
        // (`analytics.ts`), and an accidental flip to true would bury those
        // named events in noise as well as costing money.
        autocapture: false,
        // The whole product lives at `/chat`, so an automatic view event would
        // fire once and claim to describe every screen in it. Nothing here
        // needs one, and an event nobody reads is an event somebody pays for.
        capture_pageview: false,
        capture_pageleave: false,
        // Anonymous ids, not credentials, so the "tokens live in memory only"
        // invariant is not in play — and memory persistence would mint a new
        // session on every iframe reload, which chat-emp does on every handoff.
        // Replays would fragment into unwatchable pieces.
        persistence: 'localStorage',

        // Uncaught errors and rejections. Boundary-caught ones go through `captureError`.
        capture_exceptions: true,
        before_send: trimExceptionMessages,

        session_recording: {
          // The employer's own typing: search terms, closing messages, notes.
          maskAllInputs: true,
          maskTextSelector: MASK_TEXT,
          blockSelector: BLOCK,
          // NETWORK CAPTURE OFF, said outright rather than left to the
          // default. Every fetch in this app carries the session JWT, so
          // recording headers or bodies would write a live credential into a
          // replay — the "tokens live in memory only" invariant broken in the
          // most durable way there is. `replay.test.ts` fails if either flips.
          recordHeaders: false,
          recordBody: false,
          // pdf.js paints résumé pages onto a canvas. Off means the viewer
          // records as a blank rectangle with its controls intact — the CV
          // unreadable, what the employer DID with it fully visible.
          captureCanvas: { recordCanvas: false },
        },

        // Deliberately not set: `sampleRate`. It belongs to the PostHog project
        // so it can be turned down the day a bill looks wrong, without a
        // rebuild — a VITE_ value is frozen at build time. See the ticket: the
        // project MUST have a sample rate set before this ships, because
        // PostHog's own default records every session.

        // Replay must never be the reason chat breaks. Analytics is not
        // load-bearing and this is not the exception.
        on_request_error: () => {},
      });
      // chat-analytics also holds stream-chat-backend's exceptions; this tells ours apart.
      posthog.register({ app: 'chat-emp' });
      live = posthog;
      return posthog;
    })
    .catch(() => null);

  return started;
}

/**
 * Stop recording and release the connection — the chat closed or unmounted.
 * Mirrors the `disconnectUser` rule for Stream: a surface nobody is looking at
 * should not be costing anything.
 */
export function stopReplay(): void {
  live?.stopSessionRecording();
}

/** Report an error React already contained, once the SDK has loaded. A no-op if replay never starts. */
export function captureError(error: unknown): void {
  void started?.then((posthog) => posthog?.captureException(error));
}

const trim = (v: unknown) => (typeof v === 'string' ? v.slice(0, EXCEPTION_MESSAGE_MAX) : v);

function trimExceptionMessages(event: CaptureResult | null): CaptureResult | null {
  if (event?.event !== '$exception') return event;
  const props = event.properties;
  if ('$exception_message' in props) {
    props['$exception_message'] = trim(props['$exception_message']);
  }
  const list: unknown = props['$exception_list'];
  if (Array.isArray(list)) {
    for (const item of list as { value?: unknown }[]) item.value = trim(item.value);
  }
  return event;
}
